"use client";

import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  Building,
  Search,
  Mail,
  User,
  ExternalLink,
  Plus,
  FolderGit,
  Landmark,
  Sparkles,
  Pencil,
  Trash2,
  TrendingUp,
} from "lucide-react";
import Link from "next/link";

interface ClientItem {
  id: string;
  name: string;
  industry: string;
  contactName: string;
  contactEmail: string;
  logoText: string;
  projectsCount: number;
  totalRevenue: number;
}

interface ClientsClientProps {
  initialClients: ClientItem[];
}

export function ClientsClient({ initialClients }: ClientsClientProps) {
  const [clientsList, setClientsList] = useState<ClientItem[]>(initialClients);
  const [search, setSearch] = useState("");

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [projectsCount, setProjectsCount] = useState("0");
  const [totalRevenue, setTotalRevenue] = useState("0");
  const [error, setError] = useState("");

  useEffect(() => {
    setClientsList(initialClients);
  }, [initialClients]);

  const handleOpenModal = () => {
    setSelectedClientId(null);
    setName("");
    setIndustry("");
    setContactName("");
    setContactEmail("");
    setProjectsCount("0");
    setTotalRevenue("0");
    setError("");
    setIsModalOpen(true);
  };

  const handleEditClient = (client: ClientItem) => {
    setSelectedClientId(client.id);
    setName(client.name);
    setIndustry(client.industry);
    setContactName(client.contactName);
    setContactEmail(client.contactEmail);
    setProjectsCount(client.projectsCount.toString());
    setTotalRevenue(client.totalRevenue.toString());
    setError("");
    setIsModalOpen(true);
  };

  const handleCardClick = (client: ClientItem, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest("a") ||
      target.closest("button") ||
      target.closest("input") ||
      target.closest("select") ||
      target.closest("label")
    ) {
      return;
    }
    handleEditClient(client);
  };

  const handleDeleteClient = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("Sei sicuro di voler eliminare questo cliente?")) return;
    setClientsList((prev) => prev.filter((c) => c.id !== id));
  };

  const handleSubmitClient = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Il nome del cliente è obbligatorio.");
      return;
    }
    if (!industry.trim()) {
      setError("Il settore è obbligatorio.");
      return;
    }
    if (!contactName.trim()) {
      setError("Il nome del referente è obbligatorio.");
      return;
    }
    if (!contactEmail.trim() || !contactEmail.includes("@")) {
      setError("Inserire un indirizzo email valido.");
      return;
    }

    const parsedProjectsCount = parseInt(projectsCount) || 0;
    const parsedTotalRevenue = parseFloat(totalRevenue) || 0;

    if (selectedClientId) {
      setClientsList((prev) =>
        prev.map((c) =>
          c.id === selectedClientId
            ? {
                ...c,
                name,
                industry,
                contactName,
                contactEmail,
                projectsCount: parsedProjectsCount,
                totalRevenue: parsedTotalRevenue,
                logoText: name.substring(0, 2).toUpperCase(),
              }
            : c,
        ),
      );
    } else {
      const newClient: ClientItem = {
        id: `client_${Date.now()}`,
        name,
        industry,
        contactName,
        contactEmail,
        projectsCount: parsedProjectsCount,
        totalRevenue: parsedTotalRevenue,
        logoText: name.substring(0, 2).toUpperCase(),
      };
      setClientsList((prev) => [...prev, newClient]);
    }
    setIsModalOpen(false);
  };

  const getClientOwner = (clientId: string) => {
    switch (clientId) {
      case "client_1":
        return { id: "agent_giulia", name: "Giulia Bianchi", avatar: "GB" };
      case "client_2":
        return { id: "agent_elena", name: "Elena Neri", avatar: "EN" };
      case "client_3":
        return { id: "agent_alessio", name: "Alessio Corrado", avatar: "AC" };
      case "client_4":
        return { id: "agent_matteo", name: "Matteo Rossi", avatar: "MR" };
      case "client_5":
        return { id: "agent_sofia", name: "Sofia Verga", avatar: "SV" };
      default:
        const index =
          clientId
            .split("")
            .reduce((acc, char) => acc + char.charCodeAt(0), 0) % 5;
        const owners = [
          { id: "agent_giulia", name: "Giulia Bianchi", avatar: "GB" },
          { id: "agent_elena", name: "Elena Neri", avatar: "EN" },
          { id: "agent_alessio", name: "Alessio Corrado", avatar: "AC" },
          { id: "agent_matteo", name: "Matteo Rossi", avatar: "MR" },
          { id: "agent_sofia", name: "Sofia Verga", avatar: "SV" },
        ];
        return owners[index];
    }
  };

  const filteredClients = clientsList.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.industry.toLowerCase().includes(search.toLowerCase()) ||
      c.contactName.toLowerCase().includes(search.toLowerCase()),
  );

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  return (
    <div className="space-y-6">
      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca cliente per nome, settore o referente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
          <div className="text-xs text-text-secondary font-medium">
            Mostrati:{" "}
            <span className="font-semibold text-text-primary">
              {filteredClients.length}
            </span>{" "}
            di{" "}
            <span className="font-semibold text-text-primary">
              {clientsList.length}
            </span>
          </div>
          <Button onClick={handleOpenModal} className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" />
            Nuovo Cliente
          </Button>
        </div>
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
          <Building className="h-10 w-10 text-primary-500 mb-3 animate-pulse" />
          <h3 className="text-base font-bold text-text-primary">
            Nessun cliente trovato
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Non ci sono clienti corrispondenti alla ricerca selezionata.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => {
            const owner = getClientOwner(client.id);
            return (
              <Card
                key={client.id}
                onClick={(e) => handleCardClick(client, e)}
                className="relative overflow-hidden flex flex-col justify-between hover:border-primary-500/50 hover:shadow-sm transition-all group cursor-pointer"
              >
                {/* Hover action overlay for Graph, Pencil, Trash */}
                <div className="absolute top-2 right-2 flex items-center gap-1 bg-card/95 backdrop-blur-xs border border-border/50 rounded-lg p-1 shadow-xs opacity-0 group-hover:opacity-100 transition-all duration-200 z-10">
                  <Link
                    href={`/explore?focus=${client.id}`}
                    title="Visualizza nel Grafo"
                    className="p-1 hover:bg-primary-500/10 text-text-secondary hover:text-primary-600 rounded-md transition-colors"
                  >
                    <TrendingUp className="h-3.5 w-3.5" />
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClient(client);
                    }}
                    title="Modifica"
                    className="p-1 hover:bg-primary-500/10 text-text-secondary hover:text-primary-600 rounded-md transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteClient(client.id, e)}
                    title="Elimina"
                    className="p-1 hover:bg-error-soft text-text-secondary hover:text-error rounded-md transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                <CardHeader className="pb-3">
                  <div className="flex gap-4 items-start">
                    <div className="h-12 w-12 rounded-xl bg-primary-600 text-text-on-primary flex items-center justify-center font-bold text-lg shadow-sm shrink-0 select-none">
                      {client.logoText}
                    </div>
                    <div className="space-y-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge variant="neutral">{client.industry}</Badge>
                      </div>
                      <CardTitle className="text-base font-bold leading-snug truncate">
                        <Link
                          href={`/clients/${client.id}`}
                          className="hover:text-primary-600 transition-colors"
                        >
                          {client.name}
                        </Link>
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 pb-4">
                  {/* Referent detail */}
                  <div className="space-y-2 text-xs border-t border-border/30 pt-3">
                    <div className="flex items-center gap-2 text-text-secondary">
                      <User className="h-3.5 w-3.5 shrink-0" />
                      <span>
                        Referente:{" "}
                        <strong className="text-text-primary">
                          {client.contactName}
                        </strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-text-secondary">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">
                        Email:{" "}
                        <a
                          href={`mailto:${client.contactEmail}`}
                          className="hover:underline text-primary-600 font-medium"
                        >
                          {client.contactEmail}
                        </a>
                      </span>
                    </div>
                  </div>

                  {/* Performance stats */}
                  <div className="grid grid-cols-2 gap-3 bg-card-muted/40 p-3 rounded-lg border border-border/50 text-xs">
                    <div className="space-y-1">
                      <span className="text-text-muted flex items-center gap-1">
                        <FolderGit className="h-3 w-3" />
                        Progetti Attivi
                      </span>
                      <p className="font-semibold text-text-primary text-sm">
                        {client.projectsCount}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-text-muted flex items-center gap-1">
                        <Landmark className="h-3 w-3" />
                        Fatturato Totale
                      </span>
                      <p className="font-semibold text-text-primary text-sm">
                        {formatCurrency(client.totalRevenue)}
                      </p>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between items-center bg-card-muted/20 px-4 py-3 border-t border-border/20 text-xs">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Link
                      href={`/people/${owner.id}`}
                      className="flex items-center gap-1 text-[10px] text-text-secondary hover:text-primary-600 font-medium"
                      title={`Owner: ${owner.name}`}
                    >
                      <span className="h-4 w-4 shrink-0 rounded-full bg-primary-600 text-text-on-primary flex items-center justify-center font-bold text-[8px] select-none">
                        {owner.avatar}
                      </span>
                      <span className="truncate max-w-[85px]">
                        {owner.name}
                      </span>
                    </Link>
                  </div>
                  <Link
                    href={`/clients/${client.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-primary-600 hover:text-primary-700 font-semibold gap-1 py-1 h-auto text-[11px]"
                    >
                      Profilo
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Client Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedClientId ? "Modifica Cliente" : "Aggiungi Nuovo Cliente"}
            </DialogTitle>
            <DialogDescription>
              {selectedClientId
                ? "Modifica le informazioni anagrafiche del cliente."
                : "Inserisci un nuovo cliente nell'anagrafica aziendale."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitClient} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="client-name">Nome Azienda</Label>
              <Input
                id="client-name"
                placeholder="es. Acme Corporation"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="client-industry">Settore / Industria</Label>
              <Input
                id="client-industry"
                placeholder="es. Cyber Security & SaaS"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="client-contactName">Nome Referente</Label>
                <Input
                  id="client-contactName"
                  placeholder="es. Mario Rossi"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="client-contactEmail">Email Referente</Label>
                <Input
                  id="client-contactEmail"
                  type="email"
                  placeholder="es. m.rossi@acme.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="client-projects">Progetti Attivi</Label>
                <Input
                  id="client-projects"
                  type="number"
                  placeholder="0"
                  value={projectsCount}
                  onChange={(e) => setProjectsCount(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="client-revenue">Fatturato Totale (€)</Label>
                <Input
                  id="client-revenue"
                  type="number"
                  placeholder="0"
                  value={totalRevenue}
                  onChange={(e) => setTotalRevenue(e.target.value)}
                />
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
              >
                Annulla
              </Button>
              <Button type="submit">
                {selectedClientId ? "Salva Modifiche" : "Aggiungi Cliente"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
